import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  getBCAPendingIcon,
  getCrossTickIcon,
  getPersonIcon,
  getAssignIcon,
  getViewIcon,
  getTrialAssignIcon,
} from "../MemberReportMocks.js";
import { convertTo12HourFormat } from "../../../common-data/Converts.js";
import CalendarLoader from "../../../common-components/CalendarLoader.jsx";
import { useMemberPopoverData } from "./useMemberPopoverData.js";
import { CalendarIcon, XIcon } from "../../../../images/icons/icons.js";

const MemberPopover = ({
  isOpen,
  onClose,
  popoverType,
  gymId,
  memberCount = 0,
  filters,
  userInfoData,
  position = { top: 0, left: 0 },
  setModValues,
}) => {
  const {
    gymSegmentMembersBP,
    gymSegmentMembersBPLoading,
    gymSegmentMembersBPError,

    gymSegmentMembersTrial,
    gymSegmentMembersTrialLoading,
    gymSegmentMembersTrialError,

    gymSegmentMembersUnassigned,
    gymSegmentMembersUnassignedLoading,
    gymSegmentMembersUnassignedError,
  } = useSelector((state) => state.prospects);

  const [memberData, setMemberData] = useState([]);

  useMemberPopoverData(
    isOpen,
    popoverType,
    gymId,
    memberCount,
    filters,
    userInfoData
  );

  const getPopoverTitle = () => {
    switch (popoverType) {
      case "unassigned":
        return "Unassigned Members";
      case "trial":
        return "Trial Members";
      case "bca_pending":
        return "BCA Pending Members";
      default:
        return "Members";
    }
  };

  const getPopoverIcon = () => {
    switch (popoverType) {
      case "unassigned":
        return getCrossTickIcon();
      case "trial":
        return getPersonIcon();
      case "bca_pending":
        return getBCAPendingIcon();
      default:
        return getPersonIcon();
    }
  };

  const getPopoverStyle = () => {
    const isMobile = window.innerWidth <= 768;
    const viewportWidth = window.innerWidth;
    // const viewportHeight = window.innerHeight;
    const popoverWidth = isMobile ? 280 : 320;
    const popoverHeight = 400;

    let left = position.left;
    let top = position.top;

    if (left + popoverWidth / 2 > viewportWidth - 10) {
      left = viewportWidth - popoverWidth / 2 - 10;
    } else if (left - popoverWidth / 2 < 10) {
      left = popoverWidth / 2 + 10;
    }

    if (top - popoverHeight < 10) {
      top = position.top + 50;
      return {
        top: `${top}px`,
        left: `${left}px`,
        transform: "translate(-50%, 0)",
      };
    } else {
      return {
        top: `${top - 10}px`,
        left: `${left}px`,
        transform: "translate(-50%, -100%)",
      };
    }
  };

  useEffect(() => {
    if (isOpen && gymId) {
      switch (popoverType) {
        case "unassigned":
          if (
            !gymSegmentMembersUnassignedLoading &&
            gymSegmentMembersUnassigned?.data
          ) {
            setMemberData(gymSegmentMembersUnassigned.data);
          } else {
            setMemberData([]);
          }
          break;
        case "trial":
          if (!gymSegmentMembersTrialLoading && gymSegmentMembersTrial?.data) {
            setMemberData(gymSegmentMembersTrial.data);
          } else {
            setMemberData([]);
          }
          break;
        case "bca_pending":
          if (!gymSegmentMembersBPLoading && gymSegmentMembersBP?.data) {
            setMemberData(gymSegmentMembersBP.data);
          } else {
            setMemberData([]);
          }
          break;
        default:
          setMemberData([]);
      }
    }
  }, [
    isOpen,
    popoverType,
    gymId,
    gymSegmentMembersUnassigned,
    gymSegmentMembersUnassignedLoading,
    gymSegmentMembersTrial,
    gymSegmentMembersTrialLoading,
    gymSegmentMembersBP,
    gymSegmentMembersBPLoading,
  ]);

  if (!isOpen) return null;

  return (
    <>
      <div className="mr-popover-backdrop" onClick={onClose}></div>

      <div className="mr-popover" style={getPopoverStyle()}>
        <div className="mr-popover-header">
          <div className="mr-popover-title">
            {getPopoverIcon()}
            <div>
              <h3>{getPopoverTitle()}</h3>
              <span className="mr-popover-count">
                {memberData.length} of {memberCount} members
              </span>
            </div>
          </div>
          <button className="mr-popover-close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="mr-popover-body">
          {gymSegmentMembersUnassignedLoading ||
          gymSegmentMembersTrialLoading ||
          gymSegmentMembersBPLoading ? (
            <CalendarLoader daysCount={1} shiftsPerDay={2} />
          ) : memberData.length > 0 ? (
            <div className="mr-popover-members">
              {memberData.map((member) => (
                <div key={member.user_id} className="mr-popover-member">
                  <div className="mr-popover-member-info">
                    <div
                      className="mr-popover-member-name"
                      title={member.user_name}
                    >
                      {member.user_name}
                    </div>

                    <div className="mr-popover-member-details">
                      <div
                        className="mr-popover-member-mobile"
                        title={member.user_mobile}
                      >
                        📱 {member.user_mobile}
                      </div>
                      {member.member_checkin_time && (
                        <div className="mr-popover-member-time">
                          🕐 {convertTo12HourFormat(member.member_checkin_time)}
                        </div>
                      )}
                      {member.trial_days !== undefined &&
                      member.trial_days !== null &&
                      member?.three_day_assigned === 0 ? (
                        <div className={`mr-popover-member-trial not-assigned`}>
                          <XIcon color="#fff" /> trial not assigned
                        </div>
                      ) : member.trial_days !== undefined &&
                        member.trial_days !== null ? (
                        <div
                          className={`mr-popover-member-trial ${
                            parseInt(member.trial_days) === 1
                              ? "mr-popover-member-trial--day-1"
                              : parseInt(member.trial_days) === 2
                              ? "mr-popover-member-trial--day-2"
                              : parseInt(member.trial_days) === 3
                              ? "mr-popover-member-trial--day-3"
                              : ""
                          }`}
                        >
                          <CalendarIcon
                            color={
                              parseInt(member.trial_days) === 1
                                ? "#1d4ed8"
                                : "mr-popover-member-trial--day-2"
                                ? "#d97706"
                                : "mr-popover-member-trial--day-3"
                                ? "#be185d"
                                : "#000"
                            }
                          />{" "}
                          Trial Day {parseInt(member?.trial_days || 0)}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="mr-popover-member-status">
                    {member.trainer_name && (
                      <span
                        className="mr-popover-trainer"
                        title={member.trainer_name}
                      >
                        <b>Trainer: </b> 👨‍💼 {member.trainer_name}
                      </span>
                    )}

                    {popoverType === "unassigned" && (
                      <button
                        className="mr-popover-assign-btn"
                        onClick={() => {
                          setModValues((prevs) => ({
                            ...prevs,
                            isModOpen: true,
                            modName: "trainer_member_map",
                            rowData: member,
                          }));
                          onClose();
                        }}
                        title="Assign trainer to this member"
                      >
                        Map Trainer
                      </button>
                    )}

                    {popoverType === "bca_pending" && (
                      <button
                        className="mr-popover-view-btn"
                        onClick={() => {
                          setModValues((prevs) => ({
                            ...prevs,
                            isModOpen: true,
                            modName: "BCA_history",
                            rowData: member,
                          }));
                          onClose();
                        }}
                        title="View BCA history for this member"
                      >
                        {getViewIcon()}
                        View BCA
                      </button>
                    )}

                    {popoverType === "trial" && (
                      <button
                        className="mr-popover-assign-trial-btn"
                        onClick={() => {
                          setModValues((prevs) => ({
                            ...prevs,
                            isModOpen: true,
                            modName: "trial_assign",
                            rowData: member,
                          }));
                          onClose();
                        }}
                        title="Assign trial to this member"
                      >
                        {getTrialAssignIcon()}
                        Assign Trial
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mr-popover-empty">
              <div className="mr-popover-empty-icon">👥</div>
              <p>No members found</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default MemberPopover;
